import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { readinessSchema, type Readiness } from '@crm/contracts';
import { apiRequest, ApiRequestError } from '../lib/api-client';
import { t } from '../lib/i18n';

async function fetchReadiness(): Promise<Readiness> {
  try {
    return readinessSchema.parse(await apiRequest<unknown>('/health/ready'));
  } catch (error) {
    // A not-ready API answers 503 with the same payload; that is a result, not a failure to report.
    if (error instanceof ApiRequestError && error.status === 503) {
      return { status: 'not_ready', dependencies: [] };
    }
    throw error;
  }
}

export function StatusPage(): React.JSX.Element {
  const { data, error, isPending, refetch, isFetching } = useQuery({
    queryKey: ['readiness'],
    queryFn: fetchReadiness,
    refetchInterval: 15_000,
    retry: false,
  });

  const state = isPending
    ? 'checking'
    : error
      ? 'unreachable'
      : data?.status === 'ready'
        ? 'ready'
        : 'notReady';

  return (
    <main className="page">
      <header className="page__header">
        <h1>{t('app.name')}</h1>
        <p className="muted">{t('app.tagline')}</p>
      </header>

      <section className="card" aria-labelledby="status-heading">
        <div className="card__header">
          <h2 id="status-heading">{t('status.title')}</h2>
          <span className={`badge badge--${state}`}>{t(`status.${state}`)}</span>
        </div>

        {data && data.dependencies.length > 0 && (
          <ul className="dependencies">
            {data.dependencies.map((dependency) => (
              <li key={dependency.name} className="dependencies__item">
                <span className={`dot dot--${dependency.status}`} aria-hidden="true" />
                <span className="dependencies__name">{dependency.name}</span>
                <span className="muted">
                  {t(`status.dependency.${dependency.status}`)} · {dependency.duration_ms}ms
                </span>
              </li>
            ))}
          </ul>
        )}

        {error instanceof ApiRequestError && error.requestId && (
          <p className="muted small">
            {t('status.requestId')}: <code>{error.requestId}</code>
          </p>
        )}

        <button type="button" onClick={() => void refetch()} disabled={isFetching}>
          {t('status.retry')}
        </button>
      </section>

      <section className="card">
        <h2>{t('phase.current')}</h2>
        <p className="strong">{t('phase.foundation')}</p>
        <p className="muted">{t('phase.description')}</p>
      </section>
    </main>
  );
}
